/* eslint-disable max-statements, no-console */
import * as yup from "yup";
import { Alert, Button, Form } from "react-bootstrap";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Formik } from "formik";
import PropTypes from "prop-types";
import { useMutation } from "react-query";

import saveUserProfile from "../sign-up/saveProfileMutation";
import { useAuth } from "../services/eqqo.auth.service";

const validationSchema = yup.object().shape({
	confirmPassword: yup.string().oneOf([yup.ref("newPassword"), null], "Passwords must match").required("Confirm password is required"),
	newPassword: yup.string().min(8, "Password must be at least 8 characters").required("New password is required")
});

const initialValues = {
	confirmPassword: "",
	newPassword: ""
};

const ChangePasswordForm = ({ cancelText, onCancel }) => {
	const { user } = useAuth();
	const { mutateAsync: changePassword } = saveUserProfile(useMutation, user.databaseId);
	const [successMessage, setSuccessMessage] = useState(null);
	const [errorMessage, setErrorMessage] = useState(null);
	const [canCancel, setCanCancel] = useState(false);
	const inputRef = useRef();
	const confirmRef = useRef();
	const location = useLocation();
	const navigate = useNavigate();
	useEffect(() => {
		inputRef?.current?.focus();

		if ( location.pathname !== '/profile/change-password' ) {
			setCanCancel(true);
		}
	}, [location]);

	const [submitting, setSubmitting] = useState(false);
	const onHandleSubmit = useCallback(async (values, actions) => {
		setSubmitting(true);
		try {
			await changePassword({
				password: values.newPassword
			});

			inputRef.current.value = "";
			confirmRef.current.value = "";
			actions.resetForm({
				values: initialValues
			});

			setSuccessMessage("Password changed successfully");

			if ( location.pathname === '/profile/change-password' ) {
				navigate('/profile/account', { replace: true });
				setCanCancel(true);
			}
		} catch (error) {
			/* eslint-disable no-console */
			console.error(error);
			setErrorMessage(`Failed to change password: ${error.message}`);
			setSubmitting(false);
		} finally {
			setSubmitting(false);
		}
	}, [changePassword, setSubmitting, setSuccessMessage, setErrorMessage]);

	return (
		<Formik
			initialValues={initialValues}
			onSubmit={onHandleSubmit}
			validationSchema={validationSchema}
		>
			{({
				errors,
				handleBlur,
				handleChange,
				handleSubmit,
				touched
			}) => (
				<Form onSubmit={handleSubmit}>
					{successMessage
						? <Alert variant="success">{successMessage}</Alert>
						: (
							<>
								{errorMessage ? <Alert variant="danger">{errorMessage}</Alert> : null}
								<Form.Group
									className="mb-2"
								>
									<Form.Control
										isInvalid={Boolean(errors.newPassword && touched.newPassword)}
										isValid={!errors.newPassword && touched.newPassword}
										name="newPassword"
										onBlur={handleBlur}
										onChange={handleChange}
										placeholder="New Password"
										ref={inputRef}
										type="password"
									/>
									{errors.newPassword
										? <Form.Control.Feedback type="invalid">{errors.newPassword}</Form.Control.Feedback>
										: null}
								</Form.Group>
								<Form.Group
									className="mb-2"
								>
									<Form.Control
										isInvalid={Boolean(errors.confirmPassword && touched.confirmPassword)}
										isValid={!errors.confirmPassword && touched.confirmPassword}
										name="confirmPassword"
										onBlur={handleBlur}
										onChange={handleChange}
										placeholder="Confirm Password"
										ref={confirmRef}
										type="password"
									/>
									{errors.confirmPassword
										? <Form.Control.Feedback type="invalid">{errors.confirmPassword}</Form.Control.Feedback>
										: null}
								</Form.Group>
							</>
						)}
					<div className="d-flex justify-content-between">
						{
							onCancel && canCancel
								? (
									<Button onClick={onCancel} variant="link">
										{cancelText || "Cancel"}
									</Button>
								) : null
						}
						{successMessage
							? null
							: (
								<Button disabled={submitting || Object.keys(errors).length} type="submit">
									Change Password
								</Button>
							)}
					</div>
				</Form>
			)}
		</Formik>
	);
};

ChangePasswordForm.propTypes = {
	cancelText: PropTypes.string,
	onCancel: PropTypes.func
};

ChangePasswordForm.defaultProps = {
	cancelText: "Cancel",
	onCancel: null
};

export default ChangePasswordForm;